import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
  panelName?: string
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    window.dispatchEvent(new CustomEvent('app-error', {
      detail: {
        error: error.message,
        panelName: this.props.panelName ?? 'App',
      },
    }))
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null })
  }

  handleReport = () => {
    window.dispatchEvent(new CustomEvent('app-error', {
      detail: {
        error: this.state.error?.message ?? 'Unknown error',
        panelName: this.props.panelName ?? 'App',
      },
    }))
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary" role="alert">
          <div className="error-boundary__content">
            <h2 className="error-boundary__title">Something went wrong</h2>
            <p className="error-boundary__message">
              An unexpected error occurred. Your data has been preserved.
            </p>
            {this.state.error && (
              <details className="error-boundary__details">
                <summary>Error details</summary>
                <pre className="error-boundary__stack">{this.state.error.message}</pre>
              </details>
            )}
            <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap', justifyContent: 'center' }}>
              <button
                type="button"
                className="error-boundary__btn"
                onClick={this.handleReload}
              >
                Try Again
              </button>
              <button
                type="button"
                className="error-boundary__btn error-boundary__btn--secondary"
                onClick={this.handleReport}
              >
                Report this error
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
